import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { GradientOrbs } from '@/pages/Login/components/GradientOrbs';
import whiteLogo from '@/components/images/white_logo.png';
import './AuthLayout.scss';

interface AuthLayoutProps {
  // Left column: form. Always rendered.
  form: ReactNode;
  // Right column: emblem-led brand showcase. Hidden < $auth-split-bp so the
  // mobile/tablet view is form-only and can comfortably fit a single 100dvh
  // viewport without scrolling.
  showcase?: ReactNode;
}

export const AuthLayout = ({ form, showcase }: AuthLayoutProps) => {
  const single = !showcase;

  return (
    // height = 100dvh + overflow: hidden everywhere. The layout *cannot*
    // produce a page scroller — children are constrained to fit, and the
    // SignupForm uses a two-step pattern to fit its 8-field surface inside
    // a single screen. Designed for: 360x640 mobile, 768x1024 tablet,
    // 1280x720 short laptops, and 1920x1080 monitors.
    <div className={`auth-layout${single ? ' auth-layout--single' : ''}`}>
      <GradientOrbs />
      <div className="auth-layout__grid-overlay" aria-hidden="true" />

      <header className="auth-layout__top">
        <Link to="/" className="auth-layout__brand" aria-label="ProWatchly home">
          <img src={whiteLogo} alt="ProWatchly" className="auth-layout__brand-img" />
        </Link>
        <nav className="auth-layout__top-nav" aria-label="Helpful links">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
        </nav>
      </header>

      <main className="auth-layout__shell">
        <section className="auth-layout__form-col">
          <div className="auth-layout__form-slot">{form}</div>
        </section>

        {showcase && (
          <aside className="auth-layout__showcase-col" aria-hidden="true">
            {showcase}
          </aside>
        )}
      </main>

      {/* Operator-console signature in the bottom corner. Small, mono,
          unobtrusive — communicates "this is a tool, not a marketing
          page" without taking attention from the form. */}
      <div className="auth-layout__signature" aria-hidden="true">
        <span className="auth-layout__signature-dot" />
        <span>OPERATOR CONSOLE</span>
        <span className="auth-layout__signature-sep">·</span>
        <span>v0.1</span>
      </div>
    </div>
  );
};
